import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import MUITabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component={"span"}>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `tab-${index}`,
    "aria-controls": `tabpanel-${index}`,
  };
}

const Tabs = ({ TabToValue }) => {
  const [value, setValue] = useState(0);

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  // Wrapper ref so we can find the internal scroll container MUI creates
  const tabsWrapRef = useRef(null);

  const tabNames = useMemo(() => Object.keys(TabToValue), [TabToValue]);
  const tabValues = useMemo(() => Object.values(TabToValue), [TabToValue]);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const getScrollerEl = () => {
    // MUI Tabs uses an internal scroller element with overflow
    return tabsWrapRef.current?.querySelector(".MuiTabs-scroller");
  };

  const updateScrollButtons = () => {
    const el = getScrollerEl();
    if (!el) return;

    const maxScrollLeft = el.scrollWidth - el.clientWidth;

    // small epsilon so we don't get stuck in "almost 0"
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft < maxScrollLeft - 2);
  };

  useEffect(() => {
    updateScrollButtons();

    const el = getScrollerEl();
    if (!el) return;

    const onScroll = () => updateScrollButtons();
    el.addEventListener("scroll", onScroll, { passive: true });

    // also update on resize (orientation change, etc.)
    window.addEventListener("resize", updateScrollButtons);

    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateScrollButtons);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabNames.length]);

  const scrollByAmount = (dir) => {
    const el = getScrollerEl();
    if (!el) return;

    // scroll ~60% of visible width
    const amount = Math.round(el.clientWidth * 0.6) * dir;

    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          justifyContent: "center",
          display: "flex",
          position: "relative",
        }}
      >
        {/* Mobile hint buttons (only show on small screens + when needed) */}
        {isSmall && canScrollLeft && (
          <IconButton
            onClick={() => scrollByAmount(-1)}
            size="small"
            sx={{
              position: "absolute",
              left: 6,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: "rgba(0,0,0,0.35)",
              backdropFilter: "blur(6px)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
            }}
            aria-label="Scroll tabs left"
          >
            <ChevronLeftRoundedIcon sx={{ color: "white" }} />
          </IconButton>
        )}

        {isSmall && canScrollRight && (
          <IconButton
            onClick={() => scrollByAmount(1)}
            size="small"
            sx={{
              position: "absolute",
              right: 6,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 2,
              bgcolor: "rgba(0,0,0,0.35)",
              backdropFilter: "blur(6px)",
              "&:hover": { bgcolor: "rgba(0,0,0,0.55)" },
            }}
            aria-label="Scroll tabs right"
          >
            <ChevronRightRoundedIcon sx={{ color: "white" }} />
          </IconButton>
        )}

        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            ref={tabsWrapRef}
            sx={{
              width: "100%",
              maxWidth: 900, // 👈 keeps tabs visually centered on desktop
            }}
          >
            <MUITabs
              value={value}
              onChange={handleChange}
              aria-label="tabs"
              variant="scrollable"
              scrollButtons={false}
              allowScrollButtonsMobile={false}
              centered={!isSmall} // 👈 keeps centered on larger screens
              sx={{
                px: isSmall ? 4 : 0,
                "& .MuiTabs-flexContainer": {
                  justifyContent: isSmall ? "flex-start" : "center", // 👈 true centering
                },
              }}
            >
              {tabNames.map((tabName, index) => (
                <Tab key={index} label={tabName} {...a11yProps(index)} />
              ))}
            </MUITabs>
          </Box>
        </Box>
      </Box>

      {tabValues.map((tabValue, index) => (
        <TabPanel key={index} value={value} index={index}>
          {tabValue}
        </TabPanel>
      ))}
    </Box>
  );
};

Tabs.propTypes = {
  TabToValue: PropTypes.object.isRequired,
};

export default Tabs;
